import { useState } from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import { Clock, CheckCircle2 } from 'lucide-react'
import { toast } from 'sonner'
import { DashboardLayout } from '@/components/layout'
import { PageHeader, Avatar } from '@/lib/atsUi'
import { atsNav } from '@/lib/atsNav'
import { currentTenant, currentUser, interviews } from '@/lib/mocks'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { DataList } from '@/components/ui/data-list'

export const Route = createFileRoute('/interviews')({
  component: InterviewsPage,
})

const STATUS_ORDER: Record<string, number> = {
  awaiting_feedback: 0,
  today: 1,
  upcoming: 2,
}

function dayOrder(interview: (typeof interviews)[number]): number {
  if (interview.status === 'awaiting_feedback') return 0
  if (interview.day === 'today') return 1
  return 2
}

function InterviewsPage() {
  const [items, setItems] = useState(interviews)
  const ordered = [...items].sort((a, b) => dayOrder(a) - dayOrder(b))

  function addScorecard(id: string, name: string) {
    setItems((prev) =>
      prev.map((iv) => (iv.id === id ? { ...iv, status: 'completed' as const } : iv)),
    )
    toast.success(`Scorecard saved for ${name}`)
  }

  return (
    <DashboardLayout title="Interviews" navSections={atsNav} tenant={currentTenant} user={currentUser}>
      <PageHeader
        title="Interviews"
        subtitle="Everything the agents scheduled — and the scorecards waiting on you."
      />

      <DataList
        items={ordered}
        getKey={(i) => i.id}
        pageSize={5}
        empty="No interviews scheduled."
        renderRow={(interview) => (
          <div className="flex items-center gap-4 px-4 py-3 hover:bg-muted/40 transition-colors">
            <Avatar name={interview.candidateName} />

            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <Link
                  to="/candidates/$candidateId"
                  params={{ candidateId: interview.candidateId }}
                  className="text-sm font-medium hover:underline truncate"
                >
                  {interview.candidateName}
                </Link>
                <Badge variant="info">{interview.round}</Badge>
              </div>
              <p className="text-xs text-muted-foreground mt-0.5 truncate">
                {interview.jobTitle} · {interview.client}
              </p>
            </div>

            <div className="hidden sm:flex flex-col items-end gap-1 shrink-0 text-right">
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <Clock className="h-3 w-3" />
                <span>{interview.scheduledAt}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">{interview.mode}</span>
                {interview.panel && (
                  <span className="text-xs text-muted-foreground">· {interview.panel}</span>
                )}
              </div>
            </div>

            {interview.status === 'awaiting_feedback' && (
              <Button
                size="sm"
                variant="outline"
                className="shrink-0"
                onClick={() => addScorecard(interview.id, interview.candidateName)}
              >
                Add scorecard
              </Button>
            )}
            {interview.status === 'completed' && (
              <span className="flex shrink-0 items-center gap-1 text-xs font-medium text-success">
                <CheckCircle2 className="h-3.5 w-3.5" />
                Scored
              </span>
            )}
          </div>
        )}
      />
    </DashboardLayout>
  )
}
